import React, { useEffect, useRef } from 'react';
import './hero.scss';
import { gsap } from 'gsap';
import mapD from '../../assets/hero/mapDG.png';
import mapM from '../../assets/hero/mapMG.png';
import { ReactComponent as HotspotsD } from '../../assets/hero/mapD.svg';
import { ReactComponent as HotspotsM } from '../../assets/hero/mapM.svg';

function Hero({ videoSrc, navRef }) {
  const titleRef = useRef();
  const copy1Ref = useRef();
  const copy2Ref = useRef();
  const scrollDRef = useRef();
  const scrollMRef = useRef();
  const mapRef = useRef();

  useEffect(() => {
    const words = gsap.utils.selector(titleRef);
    gsap.set(
      [
        navRef.current,
        copy1Ref.current,
        copy2Ref.current,
        scrollDRef.current,
        scrollMRef.current,
        mapRef.current
      ],
      { opacity: 0 }
    );
    const tl = gsap.timeline();
    tl.to(mapRef.current, { opacity: 1, duration: 0.7, delay: 0.5 })
      .to(
        words('.words'),
        {
          opacity: 1,
          scale: 1,
          duration: 0.9,
          stagger: 0.3
        },
        '<'
      )
      .to(copy1Ref.current, { opacity: 1, duration: 0.7 }, '-=0.5')
      .to(copy2Ref.current, { opacity: 1, duration: 0.7 }, '<')
      .to(scrollDRef.current, { opacity: 1, duration: 0.7 }, '-=0.5')
      .to(scrollMRef.current, { opacity: 1, duration: 0.7 }, '<')
      .to(navRef.current, { opacity: 1, duration: 0.7 }, '<');
  }, [navRef]);

  return (
    <section className="hero" id="hero">
      <img className="hero__map" src={videoSrc === 'large' ? mapD : mapM} alt="" ref={mapRef} />
      {videoSrc === 'large' ? <HotspotsD /> : <HotspotsM />}
      <div className="hero__content">
        <h1 className="hero__content-title" ref={titleRef}>
          <span className="words">This</span> <span className="words">is</span>{' '}
          <span className="words">Canada</span>
        </h1>
        <p className="copy" ref={copy1Ref}>
          Canada is a country of wide open spaces, and wide open hearts. With its incredible
          diversity of cultures, vibrant cities, unique activities, foods, and natural beauty,
          Canada invites visitors to see life from a whole new point of view.
        </p>
        <p className="copy" ref={copy2Ref}>
          Follow our travelers as they embark on a life-changing trip up north, and be inspired to
          find your own space – for renewal, reconnection, and revelation – in this wide and
          embracing country.
        </p>
        <div className="hero__scroll hero__scroll-mobile" ref={scrollMRef}>
          <p className="hero__scroll__cap">SCROLL DOWN</p>
          <svg
            className="hero__scroll__mouse"
            width="31"
            height="45"
            viewBox="0 0 31 45"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M16.8402 45H14.1598C6.63402 45 0.5 38.866 0.5 31.3402V13.6598C0.5 6.13402 6.63402 0 14.1598 0H16.8402C24.366 0 30.5 6.13402 30.5 13.6598V31.3402C30.5 38.866 24.366 45 16.8402 45ZM14.1598 2.57732C8.07732 2.57732 3.07732 7.52577 3.07732 13.6598V31.3402C3.07732 37.4742 8.07732 42.4227 14.1598 42.4227H16.8402C22.9742 42.4227 27.9227 37.4227 27.9227 31.3402V13.6598C27.9227 7.52577 22.9227 2.57732 16.8402 2.57732H14.1598Z"
              fill="white"
            />
            <path
              d="M21.8916 23.8661C21.4277 23.3506 20.603 23.3506 20.0875 23.8661L16.7885 27.4743V14.3816C16.7885 13.6599 16.2215 13.0929 15.4999 13.0929C14.7782 13.0929 14.2112 13.6599 14.2112 14.3816V27.5259L10.9122 23.9176C10.4483 23.4022 9.62357 23.4022 9.10811 23.9176C8.69574 24.3816 8.69574 25.1032 9.10811 25.5671L14.572 31.598C15.0359 32.1135 15.8607 32.1135 16.3761 31.598L21.8916 25.5671C22.304 25.0517 22.304 24.33 21.8916 23.8661Z"
              fill="white"
              className="hero__scroll__mouse__arrow"
            />
          </svg>
        </div>
      </div>
      <div className="hero__scroll hero__scroll-desktop" ref={scrollDRef}>
        <p className="hero__scroll__cap">SCROLL DOWN</p>
        <svg
          className="hero__scroll__mouse"
          width="31"
          height="45"
          viewBox="0 0 31 45"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M16.8402 45H14.1598C6.63402 45 0.5 38.866 0.5 31.3402V13.6598C0.5 6.13402 6.63402 0 14.1598 0H16.8402C24.366 0 30.5 6.13402 30.5 13.6598V31.3402C30.5 38.866 24.366 45 16.8402 45ZM14.1598 2.57732C8.07732 2.57732 3.07732 7.52577 3.07732 13.6598V31.3402C3.07732 37.4742 8.07732 42.4227 14.1598 42.4227H16.8402C22.9742 42.4227 27.9227 37.4227 27.9227 31.3402V13.6598C27.9227 7.52577 22.9227 2.57732 16.8402 2.57732H14.1598Z"
            fill="white"
          />
          <path
            d="M21.8916 23.8661C21.4277 23.3506 20.603 23.3506 20.0875 23.8661L16.7885 27.4743V14.3816C16.7885 13.6599 16.2215 13.0929 15.4999 13.0929C14.7782 13.0929 14.2112 13.6599 14.2112 14.3816V27.5259L10.9122 23.9176C10.4483 23.4022 9.62357 23.4022 9.10811 23.9176C8.69574 24.3816 8.69574 25.1032 9.10811 25.5671L14.572 31.598C15.0359 32.1135 15.8607 32.1135 16.3761 31.598L21.8916 25.5671C22.304 25.0517 22.304 24.33 21.8916 23.8661Z"
            fill="white"
            className="hero__scroll__mouse__arrow"
          />
        </svg>
      </div>
    </section>
  );
}

export default Hero;
